import React, { useState, useEffect, useRef } from 'react';
import {
  AudioDevice,
  CameraDevice,
  NavigationScreen,
  PodcastChapter,
  PodcastMetadata,
  RecordingSession,
  AppSettings,
  ParticipantTrack
} from '@/types';
import { audioService } from '@/services/audioService';
import { recordingEngine } from '@/services/recordingEngine';
import { StorageService } from '@/services/storageService';
import { soundboardService, SOUNDBOARD_PRESETS } from '@/services/soundboardService';
import { formatDuration, formatFileSize, generateId, generateThumbnailFromBlob } from '@/utils/helpers';
import { OnlineMeetingRoom } from '@/components/OnlineMeetingRoom';
import { webrtcMeetingService } from '@/services/webrtcMeetingService';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Radio,
  Play,
  Pause,
  Square,
  Sparkles,
  Headphones,
  Sliders,
  Settings2,
  FolderOpen,
  Volume2,
  VolumeX,
  RefreshCw,
  BookmarkPlus,
  FileText,
  ListOrdered,
  Share2,
  Scissors,
  CheckCircle2,
  HardDrive,
  Camera,
  Layers,
  ChevronRight,
  ChevronDown,
  Clock,
  Music,
  ExternalLink,
  Info,
  Maximize2,
  Minimize2,
  Grid3X3,
  Flame,
  UserCheck,
  Disc3,
  RotateCcw,
  Users,
  User,
  SplitSquareVertical,
  Check,
  Smartphone,
  Wifi,
  Usb,
  QrCode,
  Download
} from 'lucide-react';

interface PodcastStudioScreenProps {
  settings: AppSettings;
  onNavigate: (screen: NavigationScreen) => void;
  onSelectSession?: (session: RecordingSession) => void;
  availableDiskSpaceGb: number;
  initialFormatMode?: 'vodcast-single' | 'vodcast-duo' | 'vodcast-online' | 'audio-only';
}

export const PodcastStudioScreen: React.FC<PodcastStudioScreenProps> = ({
  settings,
  onNavigate,
  onSelectSession,
  availableDiskSpaceGb,
  initialFormatMode = 'vodcast-single'
}) => {
  // Mode: 'vodcast-single' (1 Cam) | 'vodcast-duo' (2 Cams: Host & Guest Separate) | 'vodcast-online' (WebRTC Remote P2P) | 'audio-only'
  const [formatMode, setFormatMode] = useState<'vodcast-single' | 'vodcast-duo' | 'vodcast-online' | 'audio-only'>(initialFormatMode || 'vodcast-single');

  useEffect(() => {
    if (initialFormatMode) {
      setFormatMode(initialFormatMode);
    }
  }, [initialFormatMode]);

  const [remoteOnlineGuestStream, setRemoteOnlineGuestStream] = useState<MediaStream | null>(null);
  const [isOnlineConsentGranted, setIsOnlineConsentGranted] = useState(false);
  const [onlineMeetingRole, setOnlineMeetingRole] = useState<'host' | 'guest'>('host');

  // Device lists
  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [cameraDevices, setCameraDevices] = useState<CameraDevice[]>([]);
  const [isRefreshingDevices, setIsRefreshingDevices] = useState(false);
  const [isMobileCamModalOpen, setIsMobileCamModalOpen] = useState(false);

  // Helper: check if a camera is a mobile phone camera
  const isPhoneCam = (label: string) => {
    const l = (label || '').toLowerCase();
    return (
      l.includes('iriun') ||
      l.includes('droidcam') ||
      l.includes('camo') ||
      l.includes('phone') ||
      l.includes('mobile') ||
      l.includes('android') ||
      l.includes('galaxy') ||
      l.includes('pixel') ||
      l.includes('iphone') ||
      l.includes('epoccam') ||
      l.includes('connected camera')
    );
  };

  const formatCameraLabel = (label: string, index: number) => {
    const l = (label || '').toLowerCase();
    if (isPhoneCam(label)) {
      return `📱 [Phone Cam] ${label || `Phone Camera ${index + 1}`}`;
    }
    if (l.includes('cam link') || l.includes('elgato') || l.includes('usb') || l.includes('brio') || l.includes('logitech')) {
      return `📹 [External] ${label || `Camera ${index + 1}`}`;
    }
    return `📷 ${label || `Camera ${index + 1}`}`;
  };

  // Helper to identify mobile phone & external device microphones (DroidCam, Iriun, Webcams, USB mics)
  const isPhoneOrExternalMic = (label: string): boolean => {
    const l = (label || '').toLowerCase();
    return (
      l.includes('droidcam') ||
      l.includes('iriun') ||
      l.includes('epoccam') ||
      l.includes('camo') ||
      l.includes('phone') ||
      l.includes('mobile') ||
      l.includes('link to windows') ||
      l.includes('webcam') ||
      l.includes('camera') ||
      l.includes('c920') ||
      l.includes('c922') ||
      l.includes('usb audio')
    );
  };

  const formatMicLabel = (label: string, index: number) => {
    const l = (label || '').toLowerCase();
    if (isPhoneOrExternalMic(label)) {
      return `📱/📷 [Device Mic] ${label || `Device Microphone ${index + 1}`}`;
    }
    if (l.includes('usb') || l.includes('yeti') || l.includes('shure') || l.includes('rode') || l.includes('fifine') || l.includes('hyperx')) {
      return `🎙️ [Studio Mic] ${label || `Microphone ${index + 1}`}`;
    }
    return `🎙️ ${label || `Microphone ${index + 1}`}`;
  };

  const handleDownloadRawTrack = async (blobUrl?: string | null, filePath?: string | null, fallbackName?: string) => {
    const filename = fallbackName || `Podcast_Recording_${Date.now()}.mp4`;
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    if (window.electronAPI && filePath) {
      try {
        const buf = await window.electronAPI.readRecordingFile(filePath);
        const blob = new Blob([buf], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        return;
      } catch {
        window.electronAPI.showItemInFolder(filePath);
      }
    }
  };

  const handleOpenExternal = (url: string) => {
    if (window.electronAPI && typeof window.electronAPI.openExternalUrl === 'function') {
      window.electronAPI.openExternalUrl(url);
    } else {
      window.open(url, '_blank');
    }
  };

  // Host Devices
  const [selectedHostMicId, setSelectedHostMicId] = useState<string>(settings.defaultMicId || 'default');
  const [selectedHostCameraId, setSelectedHostCameraId] = useState<string>('default');

  // Guest Devices (for 2-Camera Duo Mode)
  const [selectedGuestMicId, setSelectedGuestMicId] = useState<string>('shared'); // 'shared' or specific mic deviceId
  const [selectedGuestCameraId, setSelectedGuestCameraId] = useState<string>('default');

  // Audio Controls & Filters
  const [micGain, setMicGain] = useState<number>(100);
  const [isMuted, setIsMuted] = useState(false);
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [echoCancellation, setEchoCancellation] = useState(true);
  const [audioMonitoring, setAudioMonitoring] = useState(false);
  const [liveMicLevel, setLiveMicLevel] = useState<number>(0);
  const [livePeakLevel, setLivePeakLevel] = useState<number>(0);

  // Video Controls
  const [aspectRatio, setAspectRatio] = useState<'16:9' | '9:16' | '1:1'>('16:9');
  const [showFramingGrid, setShowFramingGrid] = useState(false);
  const [videoFilter, setVideoFilter] = useState<'none' | 'warm' | 'crisp' | 'bw'>('none');

  // Streams
  const [hostCameraStream, setHostCameraStream] = useState<MediaStream | null>(null);
  const [hostMicStream, setHostMicStream] = useState<MediaStream | null>(null);
  const [guestCameraStream, setGuestCameraStream] = useState<MediaStream | null>(null);
  const [hostCameraError, setHostCameraError] = useState<string | null>(null);
  const [guestCameraError, setGuestCameraError] = useState<string | null>(null);

  // Episode Metadata
  const [showName, setShowName] = useState('Tech & Creator Horizon');
  const [episodeTitle, setEpisodeTitle] = useState('Ep 12: Building The Future of Local AI');
  const [hostName, setHostName] = useState('Host');
  const [guestName, setGuestName] = useState('Guest');
  const [seasonNum, setSeasonNum] = useState(1);
  const [episodeNum, setEpisodeNum] = useState(12);
  const [episodeNotes, setEpisodeNotes] = useState(
    "🎙️ Episode Outline:\n1. Introduction & Guest Welcome (00:00)\n2. Local AI vs Cloud Latency (04:30)\n3. Hardware & External Audio Gear Deep-dive (12:00)\n4. Audience Q&A and Key Takeaways (20:00)\n5. Wrap-up & Next Week's Topic (28:00)"
  );
  const [isNotesOpen, setIsNotesOpen] = useState(true);

  // Chapter Markers
  const [chapters, setChapters] = useState<PodcastChapter[]>([
    { id: generateId('ch'), title: 'Introduction & Hook', timestampSec: 0 }
  ]);
  const [newChapterTitle, setNewChapterTitle] = useState('');

  // Soundboard
  const [soundboardVol, setSoundboardVol] = useState<number>(0.8);
  const [activeSoundId, setActiveSoundId] = useState<string | null>(null);

  // Live Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDurationSec, setRecordingDurationSec] = useState(0);
  const [recordedBytes, setRecordedBytes] = useState(0);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isFullscreenStudio, setIsFullscreenStudio] = useState(false);

  // Post-recording Finish State (Both Host and Guest Separate Tracks)
  const [completedSession, setCompletedSession] = useState<RecordingSession | null>(null);
  const [savedHostLocalPath, setSavedHostLocalPath] = useState<string | null>(null);
  const [savedGuestLocalPath, setSavedGuestLocalPath] = useState<string | null>(null);
  const [hostBlobUrl, setHostBlobUrl] = useState<string | null>(null);
  const [guestBlobUrl, setGuestBlobUrl] = useState<string | null>(null);
  const [hostFileSize, setHostFileSize] = useState<number>(0);
  const [guestFileSize, setGuestFileSize] = useState<number>(0);

  // Refs for WebRTC & AudioContext
  const hostVideoRef = useRef<HTMLVideoElement>(null);
  const guestVideoRef = useRef<HTMLVideoElement>(null);
  const waveformCanvasRef = useRef<HTMLCanvasElement>(null);

  const hostMicStreamRef = useRef<MediaStream | null>(null);
  const guestMicStreamRef = useRef<MediaStream | null>(null);
  const hostCameraStreamRef = useRef<MediaStream | null>(null);
  const guestCameraStreamRef = useRef<MediaStream | null>(null);

  const hostMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const guestMediaRecorderRef = useRef<MediaRecorder | null>(null);

  const hostChunksRef = useRef<Blob[]>([]);
  const guestChunksRef = useRef<Blob[]>([]);

  const timerIntervalRef = useRef<any>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const monitorGainRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);

  // Master component unmount cleanup: guarantees ALL hardware camera and microphone tracks are stopped immediately
  useEffect(() => {
    return () => {
      if (hostCameraStreamRef.current) {
        hostCameraStreamRef.current.getTracks().forEach(t => t.stop());
        hostCameraStreamRef.current = null;
      }
      if (guestCameraStreamRef.current) {
        guestCameraStreamRef.current.getTracks().forEach(t => t.stop());
        guestCameraStreamRef.current = null;
      }
      if (hostMicStreamRef.current) {
        hostMicStreamRef.current.getTracks().forEach(t => t.stop());
        hostMicStreamRef.current = null;
      }
      if (guestMicStreamRef.current) {
        guestMicStreamRef.current.getTracks().forEach(t => t.stop());
        guestMicStreamRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }
      try {
        webrtcMeetingService.disconnect();
      } catch {}
    };
  }, []);

  // Refresh hardware devices (Mics & Cameras)
  const refreshDevices = async () => {
    setIsRefreshingDevices(true);
    try {
      const mics = await audioService.getAudioInputDevices();
      setAudioDevices(mics);
      if (mics.length > 0 && (selectedHostMicId === 'default' || !mics.some(m => m.deviceId === selectedHostMicId))) {
        setSelectedHostMicId(mics[0].deviceId);
      }

      const cams = await recordingEngine.getCameraDevices();
      setCameraDevices(cams);
      if (cams.length > 0 && (selectedHostCameraId === 'default' || !cams.some(c => c.deviceId === selectedHostCameraId))) {
        setSelectedHostCameraId(cams[0].deviceId);
      }

      // If multiple cameras detected, auto-assign second camera to guest
      if (cams.length >= 2) {
        if (selectedGuestCameraId === 'default' || selectedGuestCameraId === cams[0].deviceId) {
          setSelectedGuestCameraId(cams[1].deviceId);
        }
      } else if (cams.length === 1) {
        setSelectedGuestCameraId(cams[0].deviceId);
      }
    } catch (err) {
      console.warn('Error refreshing devices:', err);
    } finally {
      setIsRefreshingDevices(false);
    }
  };

  // Initial device discovery + debounced listener on hardware plug/unplug to prevent infinite reload loops
  useEffect(() => {
    refreshDevices();

    let debounceTimer: any = null;
    const handleDeviceChange = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        refreshDevices();
      }, 2500);
    };

    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
    }

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, []);

  // Host Camera stream acquisition
  useEffect(() => {
    if (formatMode === 'audio-only') {
      if (hostCameraStreamRef.current) {
        hostCameraStreamRef.current.getTracks().forEach(t => t.stop());
        hostCameraStreamRef.current = null;
      }
      setHostCameraStream(null);
      return;
    }

    let isSubscribed = true;
    setHostCameraError(null);

    const startHostCamera = async () => {
      try {
        // If the camera is already live on the selected device, do not interrupt it!
        if (hostCameraStreamRef.current && hostCameraStreamRef.current.active) {
          const currentTrack = hostCameraStreamRef.current.getVideoTracks()[0];
          if (currentTrack && currentTrack.readyState === 'live') {
            const currentSettingId = currentTrack.getSettings?.()?.deviceId;
            if (currentSettingId && selectedHostCameraId !== 'default' && currentSettingId === selectedHostCameraId) {
              return;
            }
          }
        }

        if (hostCameraStreamRef.current) {
          hostCameraStreamRef.current.getTracks().forEach(t => t.stop());
          hostCameraStreamRef.current = null;
        }

        const constraints: MediaStreamConstraints = {
          video: selectedHostCameraId && selectedHostCameraId !== 'default'
            ? {
                deviceId: { exact: selectedHostCameraId },
                width: { ideal: 1920, max: 3840 },
                height: { ideal: 1080, max: 2160 },
                frameRate: { ideal: 30, max: 60 }
              }
            : {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                frameRate: { ideal: 30 }
              },
          audio: false
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        stream.getVideoTracks().forEach(t => {
          try { t.contentHint = 'detail'; } catch {}
        });

        if (!isSubscribed) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        hostCameraStreamRef.current = stream;
        setHostCameraStream(stream);
        if (hostVideoRef.current) {
          hostVideoRef.current.srcObject = stream;
        }
      } catch (err: any) {
        if (isSubscribed) {
          console.warn('Host camera acquisition error:', err);
          setHostCameraError(err.message || 'Unable to access Host camera.');
        }
      }
    };

    startHostCamera();

    return () => {
      isSubscribed = false;
      if (hostCameraStreamRef.current) {
        hostCameraStreamRef.current.getTracks().forEach(t => t.stop());
        hostCameraStreamRef.current = null;
      }
    };
  }, [selectedHostCameraId, formatMode]);

  // Stable Memoized Local Stream for WebRTC Online Meeting (Prevents 60FPS re-render loops!)
  const onlineLocalStream = React.useMemo(() => {
    const tracks: MediaStreamTrack[] = [];
    if (hostCameraStream && hostCameraStream.getVideoTracks().length > 0) {
      const vTrack = hostCameraStream.getVideoTracks()[0];
      if (vTrack.readyState === 'live') tracks.push(vTrack);
    }
    const aTrack = hostMicStream?.getAudioTracks()[0] || hostMicStreamRef.current?.getAudioTracks()[0];
    if (aTrack && aTrack.readyState === 'live') {
      tracks.push(aTrack);
    }
    return tracks.length > 0 ? new MediaStream(tracks) : null;
  }, [hostCameraStream, hostMicStream]);

  // Combined Host MediaStream fallback
  const getHostCombinedStream = (): MediaStream | null => {
    return onlineLocalStream;
  };

  // Guest Camera stream acquisition (for Duo 2-Camera Mode & WebRTC Online Mode)
  useEffect(() => {
    if (formatMode === 'vodcast-online') {
      if (remoteOnlineGuestStream) {
        guestCameraStreamRef.current = remoteOnlineGuestStream;
        setGuestCameraStream(remoteOnlineGuestStream);
        if (guestVideoRef.current) {
          guestVideoRef.current.srcObject = remoteOnlineGuestStream;
        }
      } else {
        guestCameraStreamRef.current = null;
        setGuestCameraStream(null);
      }
      return;
    }

    if (formatMode !== 'vodcast-duo') {
      if (guestCameraStreamRef.current) {
        guestCameraStreamRef.current.getTracks().forEach(t => t.stop());
        guestCameraStreamRef.current = null;
      }
      setGuestCameraStream(null);
      return;
    }

    let isSubscribed = true;
    setGuestCameraError(null);

    const startGuestCamera = async () => {
      try {
        if (guestCameraStreamRef.current) {
          guestCameraStreamRef.current.getTracks().forEach(t => t.stop());
          guestCameraStreamRef.current = null;
        }

        const constraints: MediaStreamConstraints = {
          video: selectedGuestCameraId && selectedGuestCameraId !== 'default'
            ? {
                deviceId: { exact: selectedGuestCameraId },
                width: { ideal: 1920, max: 3840 },
                height: { ideal: 1080, max: 2160 },
                frameRate: { ideal: 30, max: 60 }
              }
            : {
                width: { ideal: 1920 },
                height: { ideal: 1080 },
                frameRate: { ideal: 30 }
              },
          audio: false
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!isSubscribed) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        guestCameraStreamRef.current = stream;
        setGuestCameraStream(stream);
        if (guestVideoRef.current) {
          guestVideoRef.current.srcObject = stream;
        }
      } catch (err: any) {
        if (isSubscribed) {
          console.warn('Guest camera acquisition error:', err);
          setGuestCameraError(err.message || 'Unable to access Guest camera. Make sure 2 cameras are connected.');
        }
      }
    };

    startGuestCamera();

    return () => {
      isSubscribed = false;
      if (guestCameraStreamRef.current) {
        guestCameraStreamRef.current.getTracks().forEach(t => t.stop());
        guestCameraStreamRef.current = null;
      }
    };
  }, [selectedGuestCameraId, formatMode, remoteOnlineGuestStream]);

  // Sync video elements with streams
  useEffect(() => {
    if (hostVideoRef.current && hostCameraStream) {
      hostVideoRef.current.srcObject = hostCameraStream;
    }
  }, [hostCameraStream]);

  useEffect(() => {
    if (guestVideoRef.current && guestCameraStream) {
      guestVideoRef.current.srcObject = guestCameraStream;
    }
  }, [guestCameraStream]);

  // Setup Microphone & Audio processing pipeline (Gain, Analyser, Monitoring)
  useEffect(() => {
    let isSubscribed = true;

    const setupAudio = async () => {
      try {
        if (hostMicStreamRef.current) {
          hostMicStreamRef.current.getTracks().forEach(t => t.stop());
        }

        const audioConstraints: MediaTrackConstraints = {
          deviceId: selectedHostMicId && selectedHostMicId !== 'default' ? { exact: selectedHostMicId } : undefined,
          echoCancellation,
          noiseSuppression,
          autoGainControl: false,
          sampleRate: 48000,
          channelCount: 2
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false
        });

        if (!isSubscribed) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }

        hostMicStreamRef.current = stream;
        setHostMicStream(stream);

        // Web Audio Context setup for Gain + Analyser + Headphone monitoring
        const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          audioContextRef.current = new AudioCtxClass();
        }
        const ctx = audioContextRef.current;
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }

        const sourceNode = ctx.createMediaStreamSource(stream);
        const gainNode = ctx.createGain();
        gainNode.gain.setValueAtTime(micGain / 100, ctx.currentTime);
        gainNodeRef.current = gainNode;

        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;

        sourceNode.connect(gainNode);
        gainNode.connect(analyser);

        // Headphone monitoring output
        const monitorGain = ctx.createGain();
        monitorGain.gain.setValueAtTime(audioMonitoring ? 1 : 0, ctx.currentTime);
        monitorGainRef.current = monitorGain;
        gainNode.connect(monitorGain);
        monitorGain.connect(ctx.destination);

        // Guest mic if configured
        if (selectedGuestMicId && selectedGuestMicId !== 'shared' && selectedGuestMicId !== 'default') {
          try {
            const guestStream = await navigator.mediaDevices.getUserMedia({
              audio: { deviceId: { exact: selectedGuestMicId } },
              video: false
            });
            guestMicStreamRef.current = guestStream;
          } catch (e) {
            console.warn('Guest mic setup failed, falling back to shared:', e);
          }
        }

        startAudioMeterLoop();
      } catch (err) {
        console.error('Failed to setup microphone audio graph:', err);
      }
    };

    setupAudio();

    return () => {
      isSubscribed = false;
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
      if (hostMicStreamRef.current) {
        hostMicStreamRef.current.getTracks().forEach(t => t.stop());
      }
      setHostMicStream(null);
      if (guestMicStreamRef.current) {
        guestMicStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, [selectedHostMicId, selectedGuestMicId, echoCancellation, noiseSuppression]);

  // Adjust Gain Node on slider change
  useEffect(() => {
    if (gainNodeRef.current && audioContextRef.current) {
      gainNodeRef.current.gain.setTargetAtTime(isMuted ? 0 : micGain / 100, audioContextRef.current.currentTime, 0.05);
    }
  }, [micGain, isMuted]);

  // Adjust Headphone Monitoring gain
  useEffect(() => {
    if (monitorGainRef.current && audioContextRef.current) {
      monitorGainRef.current.gain.setTargetAtTime(audioMonitoring ? 1 : 0, audioContextRef.current.currentTime, 0.05);
    }
  }, [audioMonitoring]);

  // Audio meter and waveform animation loop
  const startAudioMeterLoop = () => {
    const dataArray = new Uint8Array(128);

    const render = () => {
      if (analyserRef.current) {
        analyserRef.current.getByteFrequencyData(dataArray);

        let sum = 0;
        let peak = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = dataArray[i];
          sum += val;
          if (val > peak) peak = val;
        }

        const avg = sum / dataArray.length;
        const normalized = Math.min(100, Math.round((avg / 255) * 180));
        const peakNorm = Math.min(100, Math.round((peak / 255) * 100));

        setLiveMicLevel(normalized);
        setLivePeakLevel(prev => Math.max(peakNorm, Math.round(prev * 0.95)));

        // Render waveform canvas (for audio-only mode visualizer)
        if (waveformCanvasRef.current) {
          const canvas = waveformCanvasRef.current;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            const barWidth = (canvas.width / dataArray.length) * 2;
            let x = 0;

            for (let i = 0; i < dataArray.length; i++) {
              const barHeight = (dataArray[i] / 255) * canvas.height * 0.85;

              const gradient = ctx.createLinearGradient(0, canvas.height, 0, canvas.height - barHeight);
              gradient.addColorStop(0, '#7C5CFC');
              gradient.addColorStop(0.7, '#32D6C5');
              gradient.addColorStop(1, '#FF4D67');

              ctx.fillStyle = gradient;
              ctx.fillRect(x, (canvas.height - barHeight) / 2, barWidth - 1, barHeight);
              x += barWidth;
            }
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(render);
  };

  // Soundboard Trigger
  const handlePlaySound = (id: string) => {
    setActiveSoundId(id);
    soundboardService.playSound(id);
    setTimeout(() => {
      setActiveSoundId(null);
    }, 1500);
  };

  const handleSoundboardVolumeChange = (vol: number) => {
    setSoundboardVol(vol);
    soundboardService.setVolume(vol);
  };

  // Add Chapter Marker during live recording
  const handleAddChapter = (titleOverride?: string) => {
    const title = titleOverride || newChapterTitle.trim() || `Chapter ${chapters.length + 1}`;
    const newChap: PodcastChapter = {
      id: generateId('ch'),
      title,
      timestampSec: recordingDurationSec
    };
    setChapters(prev => [...prev, newChap]);
    setNewChapterTitle('');
  };

  // Fullscreen Studio Toggle and Keyboard (Esc) Listener
  const toggleFullscreenStudio = () => {
    setIsFullscreenStudio(prev => {
      const next = !prev;
      if (next) {
        if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
          document.documentElement.requestFullscreen().catch(() => {});
        }
      } else {
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
      }
      return next;
    });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreenStudio) {
        setIsFullscreenStudio(false);
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        }
      }
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreenStudio && !isRecording) {
        setIsFullscreenStudio(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreenStudio, isRecording]);

  // Start Recording sequence with 3s countdown & Auto-Fullscreen
  const handleTriggerRecording = () => {
    if (formatMode === 'vodcast-online') {
      if (onlineMeetingRole === 'guest') {
        alert('Recording is controlled exclusively by the Host on the Host PC.');
        return;
      }
      if (!guestCameraStream) {
        alert('Please connect with your remote guest before starting the recording.');
        return;
      }
      if (!isOnlineConsentGranted) {
        alert('Recording consent has not been granted by the guest yet. Please ask for consent in the meeting room.');
        return;
      }
    }

    // Automatically expand Studio Section into Fullscreen for distraction-free broadcast
    setIsFullscreenStudio(true);
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }

    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === 1) {
          clearInterval(interval);
          startActualRecording();
          return null;
        }
        return (prev || 3) - 1;
      });
    }, 1000);
  };

  // Parallel Multi-Track MediaRecorder Start
  const startActualRecording = async () => {
    try {
      hostChunksRef.current = [];
      guestChunksRef.current = [];

      // 1. Audio Track: Extract processed audio output from Web Audio Context
      let audioTrack: MediaStreamTrack | null = null;
      if (audioContextRef.current && gainNodeRef.current) {
        const dest = audioContextRef.current.createMediaStreamDestination();
        gainNodeRef.current.connect(dest);

        const soundboardTrack = soundboardService.getStreamTrack();
        if (soundboardTrack) {
          const sbStream = new MediaStream([soundboardTrack]);
          const sbSource = audioContextRef.current.createMediaStreamSource(sbStream);
          sbSource.connect(dest);
        }

        if (dest.stream.getAudioTracks().length > 0) {
          audioTrack = dest.stream.getAudioTracks()[0];
        }
      } else if (hostMicStreamRef.current && hostMicStreamRef.current.getAudioTracks().length > 0) {
        audioTrack = hostMicStreamRef.current.getAudioTracks()[0];
      }

      if (!audioTrack) {
        alert('Please connect a working microphone to start recording.');
        return;
      }

      const isDuo = formatMode === 'vodcast-duo' || formatMode === 'vodcast-online';
      const isVideo = formatMode !== 'audio-only';

      const mimeType = isVideo
        ? (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1,mp4a.40.2')
            ? 'video/mp4;codecs=avc1,mp4a.40.2'
            : (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
                ? 'video/webm;codecs=vp9,opus'
                : 'video/webm'))
        : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');

      // 2. Setup Host Recorder (Camera 1 + Audio)
      const hostTracks: MediaStreamTrack[] = [audioTrack];
      if (isVideo && hostCameraStream && hostCameraStream.getVideoTracks().length > 0) {
        hostTracks.push(hostCameraStream.getVideoTracks()[0]);
      }
      const hostStream = new MediaStream(hostTracks);

      const hostRecorder = new MediaRecorder(hostStream, {
        mimeType,
        audioBitsPerSecond: 256000,
        videoBitsPerSecond: isVideo ? 8000000 : undefined
      });

      hostRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          hostChunksRef.current.push(e.data);
          setRecordedBytes(prev => prev + e.data.size);
        }
      };

      // 3. Setup Guest Recorder (Camera 2 + Audio) IF Duo or Online Mode is Active
      let guestRecorder: MediaRecorder | null = null;
      if (isDuo) {
        // Resolve guest video track
        let guestVideoTrack: MediaStreamTrack | null = null;
        if (guestCameraStream && guestCameraStream.getVideoTracks().length > 0) {
          guestVideoTrack = guestCameraStream.getVideoTracks()[0];
        } else if (remoteOnlineGuestStream && remoteOnlineGuestStream.getVideoTracks().length > 0) {
          guestVideoTrack = remoteOnlineGuestStream.getVideoTracks()[0];
        } else if (webrtcMeetingService.getRemoteStream() && webrtcMeetingService.getRemoteStream()!.getVideoTracks().length > 0) {
          guestVideoTrack = webrtcMeetingService.getRemoteStream()!.getVideoTracks()[0];
        }

        // Resolve guest audio track
        let guestAudioTrack: MediaStreamTrack | null = null;
        if (formatMode === 'vodcast-online') {
          if (guestCameraStream && guestCameraStream.getAudioTracks().length > 0) {
            guestAudioTrack = guestCameraStream.getAudioTracks()[0];
          } else if (remoteOnlineGuestStream && remoteOnlineGuestStream.getAudioTracks().length > 0) {
            guestAudioTrack = remoteOnlineGuestStream.getAudioTracks()[0];
          } else if (webrtcMeetingService.getRemoteStream() && webrtcMeetingService.getRemoteStream()!.getAudioTracks().length > 0) {
            guestAudioTrack = webrtcMeetingService.getRemoteStream()!.getAudioTracks()[0];
          }
        } else {
          guestAudioTrack = (guestMicStreamRef.current && guestMicStreamRef.current.getAudioTracks().length > 0)
            ? guestMicStreamRef.current.getAudioTracks()[0]
            : audioTrack.clone();
        }

        // Fallback for guest audio if missing: clone host audio so guest file is never silent
        if (!guestAudioTrack) {
          guestAudioTrack = audioTrack.clone();
        }

        // Fallback for guest video: if video is missing or delayed, synthesize a canvas track
        if (!guestVideoTrack && isVideo) {
          const canvas = document.createElement('canvas');
          canvas.width = 1280;
          canvas.height = 720;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#090d16';
            ctx.fillRect(0, 0, 1280, 720);
            ctx.fillStyle = '#a855f7';
            ctx.font = 'bold 36px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText(`${guestName || 'Remote Guest'} (Audio Track)`, 640, 360);
          }
          const canvasStream = canvas.captureStream(15);
          if (canvasStream.getVideoTracks().length > 0) {
            guestVideoTrack = canvasStream.getVideoTracks()[0];
          }
        }

        const guestTracks: MediaStreamTrack[] = [];
        if (guestVideoTrack) {
          try {
            guestVideoTrack.contentHint = 'detail';
          } catch {}
          guestTracks.push(guestVideoTrack);
        }
        if (guestAudioTrack) guestTracks.push(guestAudioTrack);

        if (guestTracks.length > 0) {
          const guestStream = new MediaStream(guestTracks);
          try {
            guestRecorder = new MediaRecorder(guestStream, {
              mimeType,
              audioBitsPerSecond: 256000,
              videoBitsPerSecond: isVideo ? 8000000 : undefined
            });
          } catch (e) {
            console.warn('[startActualRecording] guestRecorder init with mimeType failed, falling back', e);
            guestRecorder = new MediaRecorder(guestStream);
          }

          guestRecorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
              guestChunksRef.current.push(e.data);
              setRecordedBytes(prev => prev + e.data.size);
            }
          };

          guestMediaRecorderRef.current = guestRecorder;
        }
      }

      // Synchronized onstop handler: wait for BOTH host and guest recorders
      let hostDone = false;
      let guestDone = !guestRecorder;

      const checkBothDone = async () => {
        if (hostDone && guestDone) {
          console.log('[startActualRecording] Both host and guest recorders completed.', {
            hostChunks: hostChunksRef.current.length,
            guestChunks: guestChunksRef.current.length
          });
          await handleFinishRecording(mimeType, isDuo);
        }
      };

      hostRecorder.onstop = () => {
        hostDone = true;
        checkBothDone();
      };

      if (guestRecorder) {
        guestRecorder.onstop = () => {
          guestDone = true;
          checkBothDone();
        };
      }

      hostMediaRecorderRef.current = hostRecorder;

      // Start both recorders synchronously!
      hostRecorder.start(1000);
      if (guestRecorder) {
        guestRecorder.start(1000);
      }

      if (formatMode === 'vodcast-online') {
        webrtcMeetingService.forceKeyFrame();
        webrtcMeetingService.requestRemoteKeyFrame();
        webrtcMeetingService.notifyRecordingStarted();
      }

      setIsRecording(true);
      setIsPaused(false);
      setIsFullscreenStudio(true);
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      }
      setRecordingDurationSec(0);
      setRecordedBytes(0);

      if (chapters.length === 0) {
        setChapters([{ id: generateId('ch'), title: 'Introduction', timestampSec: 0 }]);
      }

      timerIntervalRef.current = setInterval(() => {
        setRecordingDurationSec(prev => {
          const nextSec = prev + 1;
          // Refresh keyframes every 3 seconds during online recording to eliminate blur & macroblock corruption
          if (formatMode === 'vodcast-online' && nextSec % 3 === 0) {
            webrtcMeetingService.requestRemoteKeyFrame();
            webrtcMeetingService.forceKeyFrame();
          }
          return nextSec;
        });
      }, 1000);
    } catch (err) {
      console.error('Failed to start MediaRecorder:', err);
      alert('Could not start recording. Please verify camera & microphone permissions.');
    }
  };

  const handlePauseResume = () => {
    if (isPaused) {
      if (hostMediaRecorderRef.current && hostMediaRecorderRef.current.state === 'paused') {
        hostMediaRecorderRef.current.resume();
      }
      if (guestMediaRecorderRef.current && guestMediaRecorderRef.current.state === 'paused') {
        guestMediaRecorderRef.current.resume();
      }
      if (formatMode === 'vodcast-online') {
        webrtcMeetingService.forceKeyFrame();
        webrtcMeetingService.requestRemoteKeyFrame();
      }
      setIsPaused(false);
      timerIntervalRef.current = setInterval(() => {
        setRecordingDurationSec(prev => {
          const nextSec = prev + 1;
          if (formatMode === 'vodcast-online' && nextSec % 3 === 0) {
            webrtcMeetingService.requestRemoteKeyFrame();
            webrtcMeetingService.forceKeyFrame();
          }
          return nextSec;
        });
      }, 1000);
    } else {
      if (hostMediaRecorderRef.current && hostMediaRecorderRef.current.state === 'recording') {
        hostMediaRecorderRef.current.pause();
      }
      if (guestMediaRecorderRef.current && guestMediaRecorderRef.current.state === 'recording') {
        guestMediaRecorderRef.current.pause();
      }
      setIsPaused(true);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }
  };

  const handleStopRecording = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);

    if (guestMediaRecorderRef.current && guestMediaRecorderRef.current.state === 'recording') {
      try {
        guestMediaRecorderRef.current.requestData();
        guestMediaRecorderRef.current.stop();
      } catch (e) {
        console.warn('Error stopping guest recorder:', e);
      }
    }
    if (hostMediaRecorderRef.current && hostMediaRecorderRef.current.state === 'recording') {
      try {
        hostMediaRecorderRef.current.requestData();
        hostMediaRecorderRef.current.stop();
      } catch (e) {
        console.warn('Error stopping host recorder:', e);
      }
    }

    if (formatMode === 'vodcast-online') {
      webrtcMeetingService.notifyRecordingStopped();
    }

    setIsRecording(false);
    setIsPaused(false);
    setIsFullscreenStudio(false);
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  };

  // Complete recording and save separate files locally
  const handleFinishRecording = async (mimeType: string, isDuo: boolean) => {
    const duration = recordingDurationSec || 1;
    const sanitizedTitle = episodeTitle.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 35);
    const dateStr = Date.now();
    const ext = formatMode === 'audio-only' ? 'webm' : (mimeType.includes('mp4') ? 'mp4' : 'webm');

    // 1. Process Host Recording Track
    const hostBlob = new Blob(hostChunksRef.current, { type: mimeType });
    const hostUrl = URL.createObjectURL(hostBlob);
    setHostBlobUrl(hostUrl);
    setHostFileSize(hostBlob.size);

    const hostFileName = isDuo
      ? `Podcast_${sanitizedTitle}_${dateStr}_HOST.${ext}`
      : `Podcast_${sanitizedTitle}_${dateStr}.${ext}`;
    let hostLocalPath = `${settings.storagePath}\\${hostFileName}`;

    if (window.electronAPI && typeof window.electronAPI.saveRecordingBlob === 'function') {
      try {
        const hostBuf = await hostBlob.arrayBuffer();
        const savedHost = await window.electronAPI.saveRecordingBlob(generateId('pod_h'), hostFileName, hostBuf);
        hostLocalPath = savedHost.filePath;
        setSavedHostLocalPath(savedHost.filePath);
      } catch (err) {
        console.warn('Electron host save fallback:', err);
        setSavedHostLocalPath(hostLocalPath);
      }
    } else {
      setSavedHostLocalPath(hostLocalPath);
    }

    // 2. Process Guest Recording Track (if Duo Mode)
    let guestLocalPath: string | null = null;
    let guestUrl: string | null = null;
    let guestSize = 0;
    let guestBlob: Blob | null = null;

    if (isDuo && guestChunksRef.current.length > 0) {
      guestBlob = new Blob(guestChunksRef.current, { type: mimeType });
      guestUrl = URL.createObjectURL(guestBlob);
      guestSize = guestBlob.size;
      setGuestBlobUrl(guestUrl);
      setGuestFileSize(guestSize);

      const guestFileName = `Podcast_${sanitizedTitle}_${dateStr}_GUEST.${ext}`;
      guestLocalPath = `${settings.storagePath}\\${guestFileName}`;

      if (window.electronAPI && typeof window.electronAPI.saveRecordingBlob === 'function') {
        try {
          const guestBuf = await guestBlob.arrayBuffer();
          const savedGuest = await window.electronAPI.saveRecordingBlob(generateId('pod_g'), guestFileName, guestBuf);
          guestLocalPath = savedGuest.filePath;
          setSavedGuestLocalPath(savedGuest.filePath);
        } catch (err) {
          console.warn('Electron guest save fallback:', err);
          setSavedGuestLocalPath(guestLocalPath);
        }
      } else {
        setSavedGuestLocalPath(guestLocalPath);
      }
    } else {
      setSavedGuestLocalPath(null);
      setGuestBlobUrl(null);
    }

    // 3. Build Podcast Metadata
    const podcastMeta: PodcastMetadata = {
      episodeTitle: episodeTitle.trim() || 'Untitled Podcast Episode',
      episodeNumber: episodeNum,
      seasonNumber: seasonNum,
      showName: showName.trim() || 'My Podcast',
      hostName: hostName.trim() || 'Host',
      guestName: isDuo ? (guestName.trim() || 'Guest') : undefined,
      format: formatMode === 'vodcast-online' ? 'vodcast-online' : (isDuo ? 'vodcast-duo' : (formatMode === 'audio-only' ? 'audio-only' : 'solo-cam')),
      chapters,
      notes: episodeNotes
    };

    // 4. Build Participant Tracks & Exported Clips for Separate Videos
    const hostPartId = generateId('p_host');
    const guestPartId = generateId('p_guest');

    const participants: ParticipantTrack[] = [
      {
        id: hostPartId,
        name: `${hostName} (Host)`,
        role: 'host',
        crop: { x: 0, y: 0, width: 1, height: 1 },
        confidence: 100,
        isIncluded: true,
        activeSpeakerRatio: isDuo ? 52 : 100,
        visibilitySegments: [{ startSec: 0, endSec: duration }]
      }
    ];

    const exportedClips: { participantId: string; filePath: string; durationSec: number; fileSize: number }[] = [
      {
        participantId: hostPartId,
        filePath: hostLocalPath,
        durationSec: duration,
        fileSize: hostBlob.size
      }
    ];

    if (isDuo && guestLocalPath) {
      participants.push({
        id: guestPartId,
        name: `${guestName} (Guest)`,
        role: 'attendee' as const,
        crop: { x: 0, y: 0, width: 1, height: 1 },
        confidence: 100,
        isIncluded: true,
        activeSpeakerRatio: 48,
        visibilitySegments: [{ startSec: 0, endSec: duration }]
      });

      exportedClips.push({
        participantId: guestPartId,
        filePath: guestLocalPath,
        durationSec: duration,
        fileSize: guestSize
      });
    }

    // 5. Build RecordingSession entities (Save BOTH Host & Guest into Recording Library)
    let hostThumb: string | undefined = undefined;
    let guestThumb: string | undefined = undefined;
    try {
      if (hostBlob && hostBlob.size > 0) {
        const t = await generateThumbnailFromBlob(hostBlob, 0.5);
        if (t) hostThumb = t;
      }
      if (guestBlob && guestBlob.size > 0) {
        const t = await generateThumbnailFromBlob(guestBlob, 0.5);
        if (t) guestThumb = t;
      }
    } catch (e) {
      console.warn('Podcast thumbnail capture error:', e);
    }

    if (isDuo && guestLocalPath) {
      // 1. Host Session record for Recording Library
      const hostSession: RecordingSession = {
        id: generateId('rec_pod_h'),
        title: `${showName} — ${episodeTitle} [HOST: ${hostName}] (S${seasonNum}E${episodeNum})`,
        createdAt: new Date().toISOString(),
        durationSec: duration,
        fileSize: hostBlob.size,
        filePath: hostLocalPath,
        blobUrl: hostUrl,
        thumbnailUrl: hostThumb,
        resolution: formatMode === 'audio-only' ? { width: 1280, height: 720 } : { width: 1920, height: 1080 },
        fps: 30,
        audioChannels: 2,
        status: 'ready',
        aspectRatio,
        detectedLayout: 'single_speaker',
        tags: ['podcast', 'host-cam', 'duo-podcast', `S${seasonNum}E${episodeNum}`],
        podcastMetadata: {
          ...podcastMeta,
          episodeTitle: `${episodeTitle} [HOST: ${hostName}]`
        },
        participants: [participants[0]],
        exportedClips: [exportedClips[0]]
      };

      // 2. Guest Session record for Recording Library
      const guestSession: RecordingSession = {
        id: generateId('rec_pod_g'),
        title: `${showName} — ${episodeTitle} [GUEST: ${guestName}] (S${seasonNum}E${episodeNum})`,
        createdAt: new Date().toISOString(),
        durationSec: duration,
        fileSize: guestSize,
        filePath: guestLocalPath,
        blobUrl: guestUrl || undefined,
        thumbnailUrl: guestThumb,
        resolution: formatMode === 'audio-only' ? { width: 1280, height: 720 } : { width: 1920, height: 1080 },
        fps: 30,
        audioChannels: 2,
        status: 'ready',
        aspectRatio,
        detectedLayout: 'single_speaker',
        tags: ['podcast', 'guest-cam', 'duo-podcast', `S${seasonNum}E${episodeNum}`],
        podcastMetadata: {
          ...podcastMeta,
          episodeTitle: `${episodeTitle} [GUEST: ${guestName}]`
        },
        participants: participants.length > 1 ? [participants[1]] : [],
        exportedClips: exportedClips.length > 1 ? [exportedClips[1]] : []
      };

      // Save BOTH into local storage so both appear as separate recordings in Recording Library!
      StorageService.addRecording(hostSession);
      StorageService.addRecording(guestSession);

      // Create combined session for the completion review dialog
      const combinedSession: RecordingSession = {
        id: generateId('rec_pod_master'),
        title: `${showName} — ${episodeTitle} (S${seasonNum}E${episodeNum})`,
        createdAt: new Date().toISOString(),
        durationSec: duration,
        fileSize: hostBlob.size + guestSize,
        filePath: hostLocalPath,
        blobUrl: hostUrl,
        thumbnailUrl: hostThumb,
        resolution: formatMode === 'audio-only' ? { width: 1280, height: 720 } : { width: 1920, height: 1080 },
        fps: 30,
        audioChannels: 2,
        status: 'ready',
        aspectRatio,
        detectedLayout: 'side_by_side_2',
        tags: ['podcast', 'duo-cam-separated', `S${seasonNum}E${episodeNum}`],
        podcastMetadata: podcastMeta,
        participants,
        exportedClips
      };

      setCompletedSession(combinedSession);
      if (onSelectSession) {
        onSelectSession(hostSession);
      }
    } else {
      const soloSession: RecordingSession = {
        id: generateId('rec_pod'),
        title: `${showName} — ${episodeTitle} (S${seasonNum}E${episodeNum})`,
        createdAt: new Date().toISOString(),
        durationSec: duration,
        fileSize: hostBlob.size,
        filePath: hostLocalPath,
        blobUrl: hostUrl,
        thumbnailUrl: hostThumb,
        resolution: formatMode === 'audio-only' ? { width: 1280, height: 720 } : { width: 1920, height: 1080 },
        fps: 30,
        audioChannels: 2,
        status: 'ready',
        aspectRatio,
        detectedLayout: 'single_speaker',
        tags: ['podcast', 'single-cam', `S${seasonNum}E${episodeNum}`],
        podcastMetadata: podcastMeta,
        participants,
        exportedClips
      };

      StorageService.addRecording(soloSession);
      setCompletedSession(soloSession);
      if (onSelectSession) {
        onSelectSession(soloSession);
      }
    }

  };

  const handleRevealInExplorer = (filePath?: string | null) => {
    const target = filePath || savedHostLocalPath;
    if (window.electronAPI && target) {
      window.electronAPI.showItemInFolder(target);
    }
  };

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-brand-bg text-brand-text select-none">
      {/* 3.. 2.. 1.. Live Countdown Overlay */}
      {countdown !== null && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-xl animate-in fade-in duration-200">
          <div className="w-36 h-36 rounded-full border-4 border-brand-accent/40 flex items-center justify-center animate-ping absolute" />
          <div className="text-8xl md:text-9xl font-black text-white font-mono tracking-tighter drop-shadow-[0_0_40px_rgba(124,92,252,0.8)]">
            {countdown}
          </div>
          <p className="mt-8 text-sm md:text-base font-bold text-brand-muted tracking-widest uppercase flex items-center gap-2">
            <Radio className="w-4 h-4 text-brand-recording animate-pulse" />
            STAND BY — GOING LIVE
          </p>
        </div>
      )}

      {/* Main Top Studio Toolbar */}
      <div className="h-14 px-6 border-b border-brand-border/60 bg-brand-surface/40 backdrop-blur-md flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-purple-600 to-brand-accent flex items-center justify-center text-white shadow-glow">
            <Mic className="w-4 h-4" />
          </div>
          <div>
            <h1 className="font-bold text-sm tracking-tight text-brand-text flex items-center gap-2">
              Podcast Studio Pro
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 font-mono font-semibold border border-purple-500/30">
                MULTI-CAM READY
              </span>
            </h1>
          </div>

          <div className="h-4 w-px bg-brand-border/60 mx-1" />

          {/* Mode Switcher: 1 Camera vs 2 Cameras (Duo Separate Tracks) vs Audio-Only */}
          <div className="flex items-center p-1 rounded-xl bg-brand-elevated border border-brand-border/60 text-xs">
            <button
              onClick={() => !isRecording && setFormatMode('vodcast-single')}
              disabled={isRecording}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg font-medium transition-all ${
                formatMode === 'vodcast-single'
                  ? 'bg-brand-surface text-brand-text shadow-sm font-semibold'
                  : 'text-brand-muted hover:text-brand-text'
              }`}
            >
              <User className="w-3.5 h-3.5 text-brand-accent" />
              <span>1 Camera (Solo)</span>
            </button>

            <button
              onClick={() => !isRecording && setFormatMode('vodcast-duo')}
              disabled={isRecording}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg font-medium transition-all ${
                formatMode === 'vodcast-duo'
                  ? 'bg-purple-600 text-white shadow-sm font-semibold'
                  : 'text-brand-muted hover:text-brand-text'
              }`}
            >
              <Users className="w-3.5 h-3.5 text-emerald-400" />
              <span>2 Cameras (Local Duo)</span>
            </button>

            <button
              onClick={() => !isRecording && setFormatMode('vodcast-online')}
              disabled={isRecording}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg font-medium transition-all ${
                formatMode === 'vodcast-online'
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm font-semibold'
                  : 'text-brand-muted hover:text-brand-text'
              }`}
            >
              <Radio className="w-3.5 h-3.5 text-cyan-300 animate-pulse" />
              <span>🌐 Online Meeting (WebRTC P2P)</span>
            </button>

            <button
              onClick={() => !isRecording && setFormatMode('audio-only')}
              disabled={isRecording}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-lg font-medium transition-all ${
                formatMode === 'audio-only'
                  ? 'bg-brand-surface text-brand-text shadow-sm font-semibold'
                  : 'text-brand-muted hover:text-brand-text'
              }`}
            >
              <Headphones className="w-3.5 h-3.5 text-brand-secondary" />
              <span>Audio-Only</span>
            </button>
          </div>
        </div>

        {/* Right Side: Storage space & Detect Hardware */}
        <div className="flex items-center gap-3">
          <button
            onClick={refreshDevices}
            disabled={isRefreshingDevices || isRecording}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border/60 text-xs text-brand-muted hover:text-brand-text transition-colors"
            title="Scan for connected USB cameras or microphones"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshingDevices ? 'animate-spin text-brand-accent' : ''}`} />
            <span className="hidden sm:inline">Detect Cameras & Mics</span>
          </button>

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-brand-elevated/60 border border-brand-border/40 text-xs text-brand-muted">
            <HardDrive className="w-3.5 h-3.5 text-brand-secondary" />
            <span className="font-mono text-[11px] text-brand-text">{availableDiskSpaceGb} GB Free</span>
          </div>
        </div>
      </div>

      {/* Main Studio Work Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left / Center Viewport: Stage & Monitor */}
        <div
          className={
            isFullscreenStudio
              ? 'fixed inset-0 z-50 bg-[#070a13] flex flex-col justify-between p-3 sm:p-5 overflow-hidden animate-in fade-in duration-200'
              : 'flex-1 flex flex-col p-6 overflow-y-auto space-y-5'
          }
        >
          {/* Top Episode Title Banner (or Broadcast Top Bar when in Fullscreen) */}
          {isFullscreenStudio ? (
            <div className="flex items-center justify-between px-4 py-2.5 rounded-2xl bg-brand-surface/90 backdrop-blur-md border border-brand-border shadow-elevated shrink-0">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${isRecording ? (isPaused ? 'bg-amber-400' : 'bg-red-500 animate-ping') : 'bg-emerald-400 animate-pulse'}`} />
                  <span className="font-bold text-xs tracking-wider uppercase text-white">
                    {isRecording ? (isPaused ? '⏸️ PAUSED' : '🔴 ON AIR / RECORDING') : 'STUDIO READY'}
                  </span>
                </div>
                {isRecording && (
                  <span className="font-mono text-sm font-bold text-red-400 bg-red-500/15 px-2.5 py-0.5 rounded-lg border border-red-500/30">
                    {formatDuration(recordingDurationSec)}
                  </span>
                )}
                <div className="h-4 w-px bg-brand-border hidden sm:block" />
                <span className="text-xs font-semibold text-brand-text truncate max-w-[200px] sm:max-w-md hidden sm:inline">
                  {showName || 'Podcast'} — {episodeTitle || 'Live Episode'} (S{seasonNum}E{episodeNum})
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 font-mono hidden md:inline border border-purple-500/30">
                  {formatMode === 'vodcast-online' ? '🌐 WebRTC Dual-Track' : formatMode === 'vodcast-duo' ? '👥 2-Cam Duo' : '👤 Single Cam'}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {isRecording && (
                  <button
                    onClick={() => handleAddChapter()}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/40 text-purple-300 text-xs font-semibold transition-all active:scale-95"
                    title="Insert Chapter Marker"
                  >
                    <BookmarkPlus className="w-3.5 h-3.5 text-purple-400" />
                    <span className="hidden md:inline">+ Chapter</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={toggleFullscreenStudio}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text text-xs font-semibold transition-colors cursor-pointer"
                  title="Exit Fullscreen (Escape)"
                >
                  <Minimize2 className="w-3.5 h-3.5 text-brand-accent" />
                  <span>Exit Fullscreen</span>
                  <span className="text-[10px] text-brand-muted hidden sm:inline">(Esc)</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-brand-surface border border-brand-border shadow-card">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted block mb-1">
                    Podcast / Show Name
                  </label>
                  <input
                    type="text"
                    value={showName}
                    disabled={isRecording}
                    onChange={(e) => setShowName(e.target.value)}
                    placeholder="Show Name"
                    className="w-full bg-brand-elevated border border-brand-border/60 rounded-lg px-2.5 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted block mb-1">
                    Episode Title
                  </label>
                  <input
                    type="text"
                    value={episodeTitle}
                    disabled={isRecording}
                    onChange={(e) => setEpisodeTitle(e.target.value)}
                    placeholder="Episode Title"
                    className="w-full bg-brand-elevated border border-brand-border/60 rounded-lg px-2.5 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent font-semibold"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted block mb-1">
                    Host Name (Camera 1)
                  </label>
                  <input
                    type="text"
                    value={hostName}
                    disabled={isRecording}
                    onChange={(e) => setHostName(e.target.value)}
                    placeholder="Host Name"
                    className="w-full bg-brand-elevated border border-brand-border/60 rounded-lg px-2.5 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted block mb-1">
                    Guest Name (Camera 2)
                  </label>
                  <input
                    type="text"
                    value={guestName}
                    disabled={isRecording}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Guest / Co-Host"
                    className="w-full bg-brand-elevated border border-brand-border/60 rounded-lg px-2.5 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2.5 shrink-0 border-t sm:border-t-0 sm:border-l border-brand-border/60 sm:pl-3 pt-2 sm:pt-0">
                {/* Season Field */}
                <div className="flex flex-col items-center">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted block mb-0.5">
                    Season
                  </label>
                  <div className="flex items-center bg-brand-elevated border border-brand-border/80 focus-within:border-brand-accent rounded-lg px-1.5 py-1 shadow-sm transition-colors">
                    <span className="font-mono text-xs font-bold text-brand-accent select-none mr-1">S</span>
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={seasonNum}
                      disabled={isRecording}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setSeasonNum(isNaN(val) || val < 1 ? 1 : val);
                      }}
                      className="w-9 bg-transparent font-mono text-xs font-bold text-brand-text focus:outline-none text-center disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      title="Season Number (Click to edit)"
                    />
                    <div className="flex flex-col ml-0.5 -mr-0.5">
                      <button
                        type="button"
                        disabled={isRecording}
                        onClick={() => setSeasonNum(prev => prev + 1)}
                        className="text-[9px] leading-[9px] text-brand-muted hover:text-brand-text disabled:opacity-40 px-0.5 py-0.2"
                        title="Increment Season"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={isRecording || seasonNum <= 1}
                        onClick={() => setSeasonNum(prev => Math.max(1, prev - 1))}
                        className="text-[9px] leading-[9px] text-brand-muted hover:text-brand-text disabled:opacity-40 px-0.5 py-0.2"
                        title="Decrement Season"
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                </div>

                {/* Episode Field */}
                <div className="flex flex-col items-center">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-muted block mb-0.5">
                    Episode
                  </label>
                  <div className="flex items-center bg-brand-elevated border border-brand-border/80 focus-within:border-brand-accent rounded-lg px-1.5 py-1 shadow-sm transition-colors">
                    <span className="font-mono text-xs font-bold text-brand-accent select-none mr-1">E</span>
                    <input
                      type="number"
                      min={1}
                      max={9999}
                      value={episodeNum}
                      disabled={isRecording}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        const newEp = isNaN(val) || val < 1 ? 1 : val;
                        setEpisodeNum(newEp);
                        setEpisodeTitle(prev => prev.replace(/^Ep\s*\d+:/i, `Ep ${newEp}:`));
                      }}
                      className="w-11 bg-transparent font-mono text-xs font-bold text-brand-text focus:outline-none text-center disabled:opacity-60 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      title="Episode Number (Click to edit)"
                    />
                    <div className="flex flex-col ml-0.5 -mr-0.5">
                      <button
                        type="button"
                        disabled={isRecording}
                        onClick={() => {
                          const nextEp = episodeNum + 1;
                          setEpisodeNum(nextEp);
                          setEpisodeTitle(prev => prev.replace(/^Ep\s*\d+:/i, `Ep ${nextEp}:`));
                        }}
                        className="text-[9px] leading-[9px] text-brand-muted hover:text-brand-text disabled:opacity-40 px-0.5 py-0.2"
                        title="Increment Episode"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        disabled={isRecording || episodeNum <= 1}
                        onClick={() => {
                          const prevEp = Math.max(1, episodeNum - 1);
                          setEpisodeNum(prevEp);
                          setEpisodeTitle(prev => prev.replace(/^Ep\s*\d+:/i, `Ep ${prevEp}:`));
                        }}
                        className="text-[9px] leading-[9px] text-brand-muted hover:text-brand-text disabled:opacity-40 px-0.5 py-0.2"
                        title="Decrement Episode"
                      >
                        ▼
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={toggleFullscreenStudio}
                  className="ml-1 p-2 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border/60 text-brand-muted hover:text-brand-text transition-colors cursor-pointer self-end mb-0.5"
                  title="Expand Studio to Fullscreen"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}


          {/* Central Live Stage: WebRTC Online Meeting OR 2-Camera Duo Split View OR Single Cam OR Waveform */}
          {formatMode === 'vodcast-online' ? (
            <OnlineMeetingRoom
              localStream={onlineLocalStream}
              onRemoteStreamReady={(stream) => {
                setRemoteOnlineGuestStream(stream);
                setGuestCameraStream(stream);
                if (guestVideoRef.current) {
                  guestVideoRef.current.srcObject = stream;
                }
              }}
              onConsentStatusChange={(granted) => setIsOnlineConsentGranted(granted)}
              isRecordingActive={isRecording}
              onStartRecording={handleTriggerRecording}
              onStopRecording={handleStopRecording}
              hostName={hostName || 'Host'}
              guestName={guestName || 'Remote Guest'}
              onRoleChange={(r) => setOnlineMeetingRole(r)}
              isFullscreen={isFullscreenStudio}
              onToggleFullscreen={toggleFullscreenStudio}
            />
          ) : (
            <div className={`relative flex-1 rounded-3xl bg-black border border-brand-border/80 shadow-2xl overflow-hidden flex items-center justify-center p-2 ${
              isFullscreenStudio ? 'min-h-0 h-full my-2' : 'min-h-[380px]'
            }`}>
            {formatMode === 'vodcast-duo' ? (
              // 👥 2-CAMERA DUO STAGE: Side-by-side Dual Live View (Host & Guest Separate Feeds)
              <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 gap-3 items-center justify-center overflow-hidden">
                {/* HOST CAMERA TILE (Camera 1) */}
                <div className="relative w-full h-full rounded-2xl overflow-hidden bg-neutral-900 border border-white/10 flex items-center justify-center">
                  {hostCameraStream && !hostCameraError ? (
                    <video
                      ref={hostVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover transition-all duration-300 scale-x-[-1] ${
                        videoFilter === 'warm' ? 'sepia-[0.18] saturate-110 contrast-105' :
                        videoFilter === 'crisp' ? 'contrast-125 saturate-105 brightness-105' :
                        videoFilter === 'bw' ? 'grayscale contrast-125' : ''
                      }`}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-4 text-center text-white/60 space-y-2">
                      <Camera className="w-8 h-8 text-brand-accent/60" />
                      <p className="text-xs font-semibold text-white">Camera 1 (Host)</p>
                      <p className="text-[10px] text-white/50">{hostCameraError || 'Select Camera 1 from right menu'}</p>
                    </div>
                  )}

                  {/* Host Live Label & Track Indicator */}
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1 rounded-full bg-black/75 backdrop-blur-md border border-white/20 text-white text-xs font-medium">
                    <span className="w-2 h-2 rounded-full bg-brand-accent animate-pulse" />
                    <span className="font-bold">{hostName} (Host)</span>
                    <span className="text-[10px] text-brand-accent bg-brand-accent/20 px-1.5 py-0.2 rounded font-mono">
                      TRACK 1
                    </span>
                  </div>
                </div>

                {/* GUEST CAMERA TILE (Camera 2) */}
                <div className="relative w-full h-full rounded-2xl overflow-hidden bg-neutral-900 border border-white/10 flex items-center justify-center">
                  {guestCameraStream && !guestCameraError ? (
                    <video
                      ref={guestVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className={`w-full h-full object-cover transition-all duration-300 scale-x-[-1] ${
                        videoFilter === 'warm' ? 'sepia-[0.18] saturate-110 contrast-105' :
                        videoFilter === 'crisp' ? 'contrast-125 saturate-105 brightness-105' :
                        videoFilter === 'bw' ? 'grayscale contrast-125' : ''
                      }`}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-4 text-center text-white/60 space-y-2">
                      <Camera className="w-8 h-8 text-emerald-400/60" />
                      <p className="text-xs font-semibold text-white">Camera 2 (Guest)</p>
                      <p className="text-[10px] text-white/50">{guestCameraError || 'Connect 2nd camera (USB / Cam Link / Webcam)'}</p>
                      <button
                        onClick={refreshDevices}
                        className="px-3 py-1 rounded-lg bg-brand-elevated border border-white/20 text-white text-[10px]"
                      >
                        Detect 2nd Camera
                      </button>
                    </div>
                  )}

                  {/* Guest Live Label & Track Indicator */}
                  <div className="absolute top-3 left-3 z-10 flex items-center gap-2 px-3 py-1 rounded-full bg-black/75 backdrop-blur-md border border-white/20 text-white text-xs font-medium">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-bold">{guestName} (Guest)</span>
                    <span className="text-[10px] text-emerald-400 bg-emerald-400/20 px-1.5 py-0.2 rounded font-mono">
                      TRACK 2
                    </span>
                  </div>
                </div>

                {/* Notice pill indicating separate recordings */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 px-4 py-1.5 rounded-full bg-black/85 backdrop-blur-md border border-purple-500/50 text-white text-xs flex items-center gap-2 shadow-xl">
                  <SplitSquareVertical className="w-3.5 h-3.5 text-purple-400" />
                  <span className="font-semibold text-[11px]">
                    2 Separate Videos Will Be Saved Locally (Host.mp4 & Guest.mp4)
                  </span>
                </div>
              </div>
            ) : formatMode === 'vodcast-single' ? (
              // 👤 SINGLE CAMERA STAGE
              <div
                className={`relative w-full h-full flex items-center justify-center overflow-hidden transition-all duration-300 ${
                  aspectRatio === '9:16' ? 'max-w-[340px] aspect-[9/16] my-auto' : 'w-full h-full'
                }`}
              >
                {hostCameraStream && !hostCameraError ? (
                  <video
                    ref={hostVideoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`w-full h-full object-cover transition-all duration-300 scale-x-[-1] ${
                      videoFilter === 'warm' ? 'sepia-[0.18] saturate-110 contrast-105' :
                      videoFilter === 'crisp' ? 'contrast-125 saturate-105 brightness-105' :
                      videoFilter === 'bw' ? 'grayscale contrast-125' : ''
                    }`}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 text-center text-white/60 space-y-3">
                    <Camera className="w-10 h-10 text-white/40" />
                    <p className="text-sm font-medium">{hostCameraError || 'Camera not accessible'}</p>
                    <button
                      onClick={refreshDevices}
                      className="px-4 py-2 rounded-xl bg-brand-accent text-white text-xs font-semibold"
                    >
                      Detect Connected Cameras
                    </button>
                  </div>
                )}

                {/* Rule of Thirds Framing Grid */}
                {showFramingGrid && (
                  <div className="absolute inset-0 pointer-events-none grid grid-cols-3 grid-rows-3 border border-white/20">
                    <div className="border-r border-b border-white/20" />
                    <div className="border-r border-b border-white/20" />
                    <div className="border-b border-white/20" />
                    <div className="border-r border-b border-white/20" />
                    <div className="border-r border-b border-white/20" />
                    <div className="border-b border-white/20" />
                    <div className="border-r border-b border-white/20" />
                    <div className="border-r border-b border-white/20" />
                    <div />
                  </div>
                )}

                {/* Host Badge */}
                <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-xs font-medium">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>{hostName}</span>
                </div>

                {/* Quick Aspect Ratio Bar */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-3 py-1.5 rounded-2xl bg-black/75 backdrop-blur-md border border-white/15 text-white text-xs">
                  <div className="flex items-center gap-1 pr-2 border-r border-white/20">
                    <button
                      onClick={() => setAspectRatio('16:9')}
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                        aspectRatio === '16:9' ? 'bg-brand-accent text-white' : 'text-white/60'
                      }`}
                    >
                      16:9
                    </button>
                    <button
                      onClick={() => setAspectRatio('9:16')}
                      className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                        aspectRatio === '9:16' ? 'bg-brand-accent text-white' : 'text-white/60'
                      }`}
                    >
                      9:16 Shorts
                    </button>
                  </div>

                  <button
                    onClick={() => setShowFramingGrid(!showFramingGrid)}
                    className="p-1 rounded text-[11px] text-white/70 hover:text-white"
                    title="Toggle Framing Grid"
                  >
                    <Grid3X3 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              // 🎧 AUDIO-ONLY STAGE WITH ANIMATED SPECTRUM
              <div className="w-full h-full flex flex-col items-center justify-center p-8 relative overflow-hidden bg-gradient-to-b from-brand-surface/40 via-black to-brand-elevated/40">
                <div
                  className="absolute w-96 h-96 rounded-full bg-brand-accent/20 blur-3xl pointer-events-none transition-all duration-150"
                  style={{
                    transform: `scale(${1 + liveMicLevel / 80})`,
                    opacity: 0.2 + liveMicLevel / 120
                  }}
                />

                <div className="relative z-10 flex flex-col items-center text-center space-y-4">
                  <div className="w-24 h-24 rounded-3xl bg-gradient-to-tr from-brand-accent to-brand-secondary flex items-center justify-center text-white shadow-2xl">
                    <Mic className="w-10 h-10" />
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white tracking-tight">{episodeTitle}</h2>
                    <p className="text-xs text-white/60 mt-1">
                      {showName} • {hostName} & {guestName}
                    </p>
                  </div>

                  <div className="w-full max-w-xl h-24 mt-4">
                    <canvas
                      ref={waveformCanvasRef}
                      width={560}
                      height={96}
                      className="w-full h-full rounded-2xl bg-black/40 border border-white/10"
                    />
                  </div>

                  <p className="text-[11px] text-white/40 tracking-wider uppercase font-mono">
                    Studio 48kHz 24-bit Lossless Audio Stream
                  </p>
                </div>
              </div>
            )}

            {/* Live Recording HUD Overlay */}
            {isRecording && (
              <div className="absolute top-4 right-4 z-30 flex items-center gap-3 px-4 py-2 rounded-2xl bg-black/85 backdrop-blur-xl border border-red-500/60 shadow-[0_0_25px_rgba(239,68,68,0.4)] text-white">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-ping shrink-0" />
                <span className="text-xs font-black tracking-widest text-red-400 uppercase">
                  {isPaused ? 'PAUSED' : 'ON AIR'}
                </span>
                <div className="h-4 w-px bg-white/20" />
                <span className="font-mono text-base font-bold tabular-nums">
                  {formatDuration(recordingDurationSec)}
                </span>
                <div className="h-4 w-px bg-white/20" />
                <span className="text-[11px] text-white/70 font-mono">
                  {formatFileSize(recordedBytes)}
                </span>
              </div>
            )}
          </div>
          )}

          {/* Master Recording Controls Deck */}
          {formatMode === 'vodcast-online' && onlineMeetingRole === 'guest' ? (
            <div className="p-4 rounded-3xl bg-brand-surface border border-brand-border shadow-elevated flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-indigo-500/15 text-indigo-400 border border-indigo-500/30">
                  <UserCheck className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-brand-text flex items-center gap-2">
                    Guest Mode Active — Recording Controlled Exclusively by Host
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-semibold border border-emerald-500/30">
                      Zero Local Storage Used
                    </span>
                  </h4>
                  <p className="text-[11px] text-brand-muted mt-0.5">
                    Both your video/audio stream and the Host's stream are recorded together strictly on the Host's PC.
                  </p>
                </div>
              </div>

              {isRecording ? (
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-red-600/15 border border-red-500/40 text-red-400 text-xs font-bold animate-pulse">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping" />
                  <span>HOST IS RECORDING BOTH PARTICIPANTS LOCALLY</span>
                </div>
              ) : (
                <div className="text-xs text-brand-muted italic flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-brand-elevated/60 border border-brand-border/60">
                  <Radio className="w-3.5 h-3.5 text-indigo-400 animate-pulse" />
                  <span>Ready & connected. Host will initiate dual-track recording.</span>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 rounded-3xl bg-brand-surface border border-brand-border shadow-elevated flex flex-wrap items-center justify-between gap-4">
              {/* Audio VU Decibel Meter */}
              <div className="flex items-center gap-3 min-w-[260px] flex-1">
                <div className="flex items-center gap-1.5 text-xs text-brand-muted shrink-0 w-24">
                  <Mic className={`w-4 h-4 ${liveMicLevel > 15 ? 'text-brand-secondary animate-pulse' : 'text-brand-muted'}`} />
                  <span className="truncate">{isMuted ? 'Muted' : 'Mic Meter'}</span>
                </div>

                <div className="flex-1 h-3 bg-brand-elevated rounded-full overflow-hidden flex items-center p-0.5 border border-brand-border/60">
                  <div
                    className="h-full rounded-full transition-all duration-75 ease-out"
                    style={{
                      width: isMuted ? '0%' : `${Math.min(100, Math.max(3, liveMicLevel))}%`,
                      background: liveMicLevel > 75
                        ? 'linear-gradient(90deg, #32D6C5 0%, #EAB308 65%, #FF4D67 95%)'
                        : 'linear-gradient(90deg, #32D6C5 0%, #7C5CFC 100%)'
                    }}
                  />
                </div>

                <span className="text-xs font-mono tabular-nums text-brand-muted w-12 text-right">
                  {isMuted ? 'OFF' : (liveMicLevel > 0 ? `-${Math.max(0, 100 - liveMicLevel)}dB` : '-inf')}
                </span>
              </div>

              {/* Central Master Record / Pause / Stop Buttons */}
              <div className="flex items-center gap-3">
                {!isRecording ? (
                  <button
                    onClick={handleTriggerRecording}
                    className="flex items-center gap-2.5 px-6 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-brand-recording hover:brightness-110 active:scale-95 text-white font-bold text-sm shadow-[0_0_30px_rgba(239,68,68,0.45)] transition-all cursor-pointer"
                  >
                    <Disc3 className="w-5 h-5 animate-spin" />
                    <span>
                      {formatMode === 'vodcast-online' ? 'START ONLINE DUAL-TRACK RECORDING' : (formatMode === 'vodcast-duo' ? 'START 2-CAMERA PODCAST RECORDING' : 'START PODCAST RECORDING')}
                    </span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handlePauseResume}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text font-semibold text-xs transition-all active:scale-95"
                    >
                      {isPaused ? <Play className="w-4 h-4 text-emerald-400" /> : <Pause className="w-4 h-4 text-amber-400" />}
                      <span>{isPaused ? 'Resume Both' : 'Pause Both'}</span>
                    </button>

                    <button
                      onClick={() => handleAddChapter()}
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40 text-xs font-bold transition-all active:scale-95"
                      title="Insert Chapter Marker at Current Time"
                    >
                      <BookmarkPlus className="w-4 h-4 text-purple-400" />
                      <span>+ Add Chapter Marker</span>
                    </button>

                    <button
                      onClick={handleStopRecording}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 active:scale-95 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
                    >
                      <Square className="w-4 h-4 fill-white" />
                      <span>FINISH RECORDING</span>
                    </button>
                  </>
                )}
              </div>

              {/* Quick Gain, Mute & Fullscreen Toggle */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className={`p-2.5 rounded-xl border transition-colors ${
                    isMuted ? 'bg-red-500/20 border-red-500/40 text-red-400' : 'bg-brand-elevated border-brand-border text-brand-muted hover:text-brand-text'
                  }`}
                  title={isMuted ? 'Unmute Microphone' : 'Mute Microphone'}
                >
                  {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4 text-brand-secondary" />}
                </button>

                <button
                  onClick={() => setAudioMonitoring(!audioMonitoring)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-medium transition-colors ${
                    audioMonitoring
                      ? 'bg-brand-secondary/20 border-brand-secondary/40 text-brand-secondary font-bold'
                      : 'bg-brand-elevated border-brand-border text-brand-muted hover:text-brand-text'
                  }`}
                  title="Hear your live mic voice in headphones (Zero latency loopback)"
                >
                  <Headphones className="w-3.5 h-3.5" />
                  <span className="hidden lg:inline">{audioMonitoring ? 'Monitor ON' : 'Monitor'}</span>
                </button>

                <button
                  type="button"
                  onClick={toggleFullscreenStudio}
                  className="p-2.5 rounded-xl border border-brand-border bg-brand-elevated hover:bg-brand-elevated/80 text-brand-muted hover:text-brand-text transition-colors cursor-pointer"
                  title={isFullscreenStudio ? 'Exit Fullscreen Studio (Esc)' : 'Expand Studio to Fullscreen'}
                >
                  {isFullscreenStudio ? <Minimize2 className="w-4 h-4 text-brand-accent" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar: Dual Hardware Controls (Host Cam & Guest Cam), Live Soundboard & Chapters */}
        <div className="w-80 md:w-96 border-l border-brand-border/60 bg-brand-surface/70 flex flex-col shrink-0 select-none overflow-y-auto">
          {/* Section 1: Hardware Devices (Host & Guest Dual Camera Setup) */}
          <div className="p-4 border-b border-brand-border/60 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-brand-text flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-brand-accent" />
                <span>Camera & Mic Assignments</span>
              </h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 font-semibold">
                {formatMode === 'vodcast-duo' ? 'Dual Tracks' : 'Single Track'}
              </span>
            </div>

            {/* Connect Phone as Camera Quick Button */}
            <button
              onClick={() => setIsMobileCamModalOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-brand-accent/20 hover:from-purple-600/30 hover:to-brand-accent/30 border border-purple-500/40 text-purple-200 text-xs font-bold transition-all cursor-pointer shadow-sm"
            >
              <Smartphone className="w-4 h-4 text-purple-400" />
              <span>📱 Connect Phone as Camera (iPhone / Android)</span>
            </button>

            {/* CAMERA 1: HOST CAMERA */}
            <div className="p-3 rounded-2xl bg-brand-elevated/70 border border-brand-border/60 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-brand-accent flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  <span>Host Camera (Camera 1)</span>
                </span>
                <span className="text-[10px] text-brand-muted font-mono">TRACK 1</span>
              </div>
              <select
                value={selectedHostCameraId}
                disabled={isRecording}
                onChange={(e) => setSelectedHostCameraId(e.target.value)}
                className="w-full bg-brand-surface border border-brand-border rounded-xl px-2.5 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent font-medium"
              >
                {cameraDevices.length === 0 ? (
                  <option value="default">Default Webcam</option>
                ) : (
                  cameraDevices.map((c, i) => (
                    <option key={c.deviceId} value={c.deviceId}>
                      {formatCameraLabel(c.label, i)}
                    </option>
                  ))
                )}
              </select>
            </div>

            {/* CAMERA 2: GUEST CAMERA (Active in Duo Mode) */}
            {formatMode === 'vodcast-duo' && (
              <div className="p-3 rounded-2xl bg-emerald-950/20 border border-emerald-500/30 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-emerald-400 flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    <span>Guest Camera (Camera 2)</span>
                  </span>
                  <span className="text-[10px] text-emerald-400 font-mono">TRACK 2</span>
                </div>
                <select
                  value={selectedGuestCameraId}
                  disabled={isRecording}
                  onChange={(e) => setSelectedGuestCameraId(e.target.value)}
                  className="w-full bg-brand-surface border border-emerald-500/40 rounded-xl px-2.5 py-1.5 text-xs text-brand-text focus:outline-none focus:border-emerald-400 font-medium"
                >
                  {cameraDevices.length <= 1 ? (
                    <option value="default">📱 Select connected Phone / 2nd Camera</option>
                  ) : (
                    cameraDevices.map((c, i) => (
                      <option key={c.deviceId} value={c.deviceId}>
                        {formatCameraLabel(c.label, i)}
                      </option>
                    ))
                  )}
                </select>

                <p className="text-[10px] text-emerald-300/80 leading-relaxed">
                  💡 Guest ki recording alag se `..._GUEST.mp4` me save hogi.
                </p>
              </div>
            )}

            {/* MICROPHONE SELECTION & DEVICE MIC DETECTION */}
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-medium text-brand-muted flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5 text-brand-accent" />
                  <span>Microphone Setup</span>
                </label>
                <button
                  onClick={refreshDevices}
                  disabled={isRefreshingDevices}
                  className="flex items-center gap-1 text-[10px] text-brand-accent hover:text-brand-accent-hover font-semibold transition-colors"
                  title="Rescan connected phone mics, webcams & USB audio"
                >
                  <RefreshCw className={`w-3 h-3 ${isRefreshingDevices ? 'animate-spin' : ''}`} />
                  <span>Scan Audio ({audioDevices.length})</span>
                </button>
              </div>

              {/* Connected Phone / Camera Microphone Quick Assignment Card */}
              {audioDevices.filter(m => isPhoneOrExternalMic(m.label)).length > 0 && (
                <div className="p-2.5 rounded-xl bg-purple-950/30 border border-purple-500/40 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-purple-300 flex items-center gap-1.5">
                      <Smartphone className="w-3.5 h-3.5 text-purple-400" />
                      <span>Phone / Camera Mic Connected!</span>
                    </span>
                    <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-emerald-500/20 text-emerald-400 font-mono font-semibold">
                      Ready
                    </span>
                  </div>

                  {audioDevices.filter(m => isPhoneOrExternalMic(m.label)).map(dev => (
                    <div key={dev.deviceId} className="flex flex-col gap-1.5 bg-black/40 p-2 rounded-lg border border-purple-500/20">
                      <span className="text-[11px] text-white font-medium truncate" title={dev.label}>
                        🎙️ {dev.label}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => setSelectedHostMicId(dev.deviceId)}
                          className={`flex-1 px-2 py-1 rounded text-[10px] font-bold transition-all ${
                            selectedHostMicId === dev.deviceId
                              ? 'bg-brand-accent text-white shadow-sm'
                              : 'bg-brand-surface hover:bg-brand-surface/80 text-brand-muted hover:text-white border border-brand-border'
                          }`}
                        >
                          {selectedHostMicId === dev.deviceId ? '✓ Host Mic (Active)' : 'Use as Host Mic'}
                        </button>

                        {formatMode === 'vodcast-duo' && (
                          <button
                            onClick={() => setSelectedGuestMicId(dev.deviceId)}
                            className={`flex-1 px-2 py-1 rounded text-[10px] font-bold transition-all ${
                              selectedGuestMicId === dev.deviceId
                                ? 'bg-emerald-600 text-white shadow-sm'
                                : 'bg-brand-surface hover:bg-brand-surface/80 text-emerald-400 hover:text-white border border-emerald-500/30'
                            }`}
                          >
                            {selectedGuestMicId === dev.deviceId ? '✓ Guest Mic (Active)' : 'Use as Guest Mic'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Primary Mic Dropdown (Host) */}
              <div className="space-y-1">
                <label className="text-[10px] font-medium text-brand-muted flex items-center justify-between">
                  <span>Host Microphone (Track 1)</span>
                  <span className="text-[10px] text-brand-accent font-semibold">{audioDevices.length} Mics</span>
                </label>
                <select
                  value={selectedHostMicId}
                  disabled={isRecording}
                  onChange={(e) => setSelectedHostMicId(e.target.value)}
                  className="w-full bg-brand-elevated border border-brand-border rounded-xl px-3 py-2 text-xs text-brand-text focus:outline-none focus:border-brand-accent font-medium"
                >
                  {audioDevices.length === 0 ? (
                    <option value="default">Default System Microphone</option>
                  ) : (
                    audioDevices.map((m, idx) => (
                      <option key={m.deviceId} value={m.deviceId}>
                        {formatMicLabel(m.label, idx)}
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Guest Mic Dropdown (Track 2) */}
              {formatMode === 'vodcast-duo' && (
                <div className="space-y-1 pt-1">
                  <label className="text-[10px] font-medium text-emerald-400 flex items-center justify-between">
                    <span>Guest Microphone (Track 2)</span>
                    <span className="text-[10px] text-emerald-400 font-semibold">Separate Audio</span>
                  </label>
                  <select
                    value={selectedGuestMicId}
                    disabled={isRecording}
                    onChange={(e) => setSelectedGuestMicId(e.target.value)}
                    className="w-full bg-brand-elevated border border-emerald-500/40 rounded-xl px-3 py-2 text-xs text-brand-text focus:outline-none focus:border-emerald-400 font-medium"
                  >
                    <option value="shared">🎙️ Shared Studio Mic (Same as Host)</option>
                    {audioDevices.map((m, idx) => (
                      <option key={m.deviceId} value={m.deviceId}>
                        {formatMicLabel(m.label, idx)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Mic Gain Slider */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px] text-brand-muted">
                <span>Microphone Gain</span>
                <span className="font-mono text-brand-text font-bold">{micGain}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="150"
                value={micGain}
                onChange={(e) => setMicGain(Number(e.target.value))}
                className="w-full accent-brand-accent h-1.5 bg-brand-elevated rounded-lg cursor-pointer"
              />
            </div>
          </div>

          {/* Section 2: Studio Live Soundboard */}
          <div className="p-4 border-b border-brand-border/60 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-brand-text flex items-center gap-1.5">
                <Music className="w-3.5 h-3.5 text-brand-secondary" />
                <span>Live SFX & Soundboard</span>
              </h3>

              <div className="flex items-center gap-2">
                <Volume2 className="w-3 h-3 text-brand-muted" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={soundboardVol}
                  onChange={(e) => handleSoundboardVolumeChange(Number(e.target.value))}
                  className="w-16 accent-brand-secondary h-1 bg-brand-elevated rounded cursor-pointer"
                  title="Soundboard Volume"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {SOUNDBOARD_PRESETS.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handlePlaySound(item.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium transition-all active:scale-95 ${
                    activeSoundId === item.id
                      ? 'bg-brand-secondary text-brand-bg font-bold border-brand-secondary shadow-glow'
                      : 'bg-brand-elevated hover:bg-brand-elevated/80 border-brand-border/60 text-brand-text'
                  }`}
                >
                  <span className="text-base">{item.emoji}</span>
                  <span className="truncate">{item.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Section 3: Live Chapter Markers */}
          <div className="p-4 border-b border-brand-border/60 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-brand-text flex items-center gap-1.5">
                <ListOrdered className="w-3.5 h-3.5 text-purple-400" />
                <span>Episode Chapters ({chapters.length})</span>
              </h3>
            </div>

            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={newChapterTitle}
                onChange={(e) => setNewChapterTitle(e.target.value)}
                placeholder="Topic / Chapter name..."
                onKeyDown={(e) => e.key === 'Enter' && handleAddChapter()}
                className="flex-1 bg-brand-elevated border border-brand-border/60 rounded-xl px-3 py-1.5 text-xs text-brand-text focus:outline-none focus:border-brand-accent"
              />
              <button
                onClick={() => handleAddChapter()}
                className="px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold transition-colors"
              >
                + Add
              </button>
            </div>

            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
              {chapters.map((ch, i) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between p-2 rounded-xl bg-brand-elevated/50 border border-brand-border/40 text-xs"
                >
                  <div className="flex items-center gap-2 truncate">
                    <span className="w-4 h-4 rounded-full bg-purple-500/20 text-purple-400 text-[10px] font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="truncate text-brand-text font-medium">{ch.title}</span>
                  </div>
                  <span className="font-mono text-[11px] text-brand-muted shrink-0 ml-2">
                    {formatDuration(ch.timestampSec)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: Live Teleprompter / Episode Notes */}
          <div className="p-4 space-y-2 flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-brand-text flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-amber-400" />
                <span>Teleprompter & Talking Points</span>
              </h3>
              <button
                onClick={() => setIsNotesOpen(!isNotesOpen)}
                className="text-brand-muted hover:text-brand-text text-xs"
              >
                {isNotesOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>

            {isNotesOpen && (
              <textarea
                value={episodeNotes}
                onChange={(e) => setEpisodeNotes(e.target.value)}
                placeholder="Write bullet points, questions, guest notes or teleprompter script here..."
                className="flex-1 w-full min-h-[140px] bg-brand-elevated border border-brand-border/60 rounded-xl p-3 text-xs text-brand-text leading-relaxed font-sans focus:outline-none focus:border-brand-accent resize-none"
              />
            )}
          </div>
        </div>
      </div>

      {/* Post-Recording Success Modal (Showing BOTH Separate Videos if Duo Mode) */}
      {completedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-4xl bg-brand-surface rounded-3xl border border-brand-border shadow-2xl p-6 md:p-8 space-y-6 max-h-[92vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-bold">
                    <span>
                      {savedGuestLocalPath
                        ? '2 SEPARATE PODCAST TRACKS RECORDED & SAVED'
                        : '100% LOCAL RECORDING READY'}
                    </span>
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-brand-text mt-1">
                    {completedSession.title}
                  </h2>
                </div>
              </div>

              <button
                onClick={() => setCompletedSession(null)}
                className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text"
              >
                ✕
              </button>
            </div>

            {/* SEPARATE VIDEO TRACKS DISPLAY (Host Track & Guest Track) */}
            <div className={`grid grid-cols-1 ${savedGuestLocalPath ? 'md:grid-cols-2' : ''} gap-4`}>
              {/* TRACK 1: HOST VIDEO FILE */}
              <div className="p-4 rounded-2xl bg-brand-elevated/70 border border-brand-border/60 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-brand-accent flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    <span>Track 1: Host Video ({hostName})</span>
                  </span>
                  <span className="font-mono text-[11px] text-brand-muted">
                    {formatFileSize(hostFileSize)}
                  </span>
                </div>

                {hostBlobUrl && (
                  <div className="rounded-xl overflow-hidden bg-black border border-white/10 aspect-video flex items-center justify-center">
                    <video src={hostBlobUrl} controls className="w-full h-full object-contain" />
                  </div>
                )}

                <div className="space-y-1">
                  <span className="text-[10px] text-brand-muted uppercase font-mono block">Saved File Path:</span>
                  <p className="font-mono text-xs text-brand-text truncate bg-brand-surface p-2 rounded-lg border border-brand-border/40">
                    {savedHostLocalPath}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleRevealInExplorer(savedHostLocalPath)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brand-surface hover:bg-brand-surface/80 border border-brand-border text-brand-text text-xs font-semibold transition-colors"
                  >
                    <FolderOpen className="w-3.5 h-3.5 text-brand-accent" />
                    <span>Show in Folder</span>
                  </button>

                  <button
                    onClick={() => handleDownloadRawTrack(hostBlobUrl, savedHostLocalPath, `Podcast_${episodeTitle.replace(/[^a-zA-Z0-9_-]/g, '_')}_HOST.mp4`)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-sm transition-all active:scale-95 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Download Raw MP4</span>
                  </button>
                </div>
              </div>

              {/* TRACK 2: GUEST VIDEO FILE (If recorded in Duo mode) */}
              {savedGuestLocalPath && (
                <div className="p-4 rounded-2xl bg-emerald-950/20 border border-emerald-500/40 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
                      <Users className="w-4 h-4" />
                      <span>Track 2: Guest Video ({guestName})</span>
                    </span>
                    <span className="font-mono text-[11px] text-emerald-300">
                      {formatFileSize(guestFileSize)}
                    </span>
                  </div>

                  {guestBlobUrl && (
                    <div className="rounded-xl overflow-hidden bg-black border border-white/10 aspect-video flex items-center justify-center">
                      <video src={guestBlobUrl} controls className="w-full h-full object-contain" />
                    </div>
                  )}

                  <div className="space-y-1">
                    <span className="text-[10px] text-emerald-300 uppercase font-mono block">Saved File Path:</span>
                    <p className="font-mono text-xs text-brand-text truncate bg-brand-surface p-2 rounded-lg border border-brand-border/40">
                      {savedGuestLocalPath}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleRevealInExplorer(savedGuestLocalPath)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brand-surface hover:bg-brand-surface/80 border border-emerald-500/40 text-brand-text text-xs font-semibold transition-colors"
                    >
                      <FolderOpen className="w-3.5 h-3.5 text-emerald-400" />
                      <span>Show in Folder</span>
                    </button>

                    <button
                      onClick={() => handleDownloadRawTrack(guestBlobUrl, savedGuestLocalPath, `Podcast_${episodeTitle.replace(/[^a-zA-Z0-9_-]/g, '_')}_GUEST.mp4`)}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold shadow-sm transition-all active:scale-95 cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Download Raw MP4</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Technical details badge row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 rounded-2xl bg-brand-elevated border border-brand-border/60 text-xs">
              <div>
                <span className="text-[10px] text-brand-muted uppercase block">Duration</span>
                <span className="font-mono font-bold text-brand-text text-sm">
                  {formatDuration(completedSession.durationSec)}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-brand-muted uppercase block">Total Size</span>
                <span className="font-mono font-bold text-brand-text text-sm">
                  {formatFileSize(completedSession.fileSize)}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-brand-muted uppercase block">Camera Mode</span>
                <span className="font-bold text-brand-text text-sm capitalize">
                  {savedGuestLocalPath ? 'Dual Separate Tracks' : 'Single Cam'}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-brand-muted uppercase block">Chapters</span>
                <span className="font-bold text-purple-400 text-sm">
                  {completedSession.podcastMetadata?.chapters?.length || 0} Bookmarks
                </span>
              </div>
            </div>

            {/* Next Step Action Buttons */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
              <button
                onClick={() => {
                  setCompletedSession(null);
                  setRecordingDurationSec(0);
                  setRecordedBytes(0);
                  // Auto-increment episode for next session
                  setEpisodeNum(prev => {
                    const nextEp = prev + 1;
                    setEpisodeTitle(old => old.replace(/^Ep\s*\d+:/i, `Ep ${nextEp}:`));
                    return nextEp;
                  });
                }}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text text-xs font-semibold transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5 text-brand-muted" />
                <span>Record Another Episode</span>
              </button>


              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    handleRevealInExplorer(savedHostLocalPath);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-brand-surface hover:bg-brand-surface/80 border border-brand-border text-brand-text text-xs font-semibold transition-colors"
                >
                  <FolderOpen className="w-3.5 h-3.5 text-brand-accent" />
                  <span>Show in Windows Explorer</span>
                </button>

                <button
                  onClick={() => {
                    onNavigate('participant-review');
                  }}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold shadow-md transition-all active:scale-95"
                >
                  <Scissors className="w-3.5 h-3.5" />
                  <span>Open in Video Editor</span>
                </button>

                <button
                  onClick={() => {
                    onNavigate('library');
                  }}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-brand-surface hover:bg-brand-elevated border border-brand-border text-brand-text text-xs font-semibold transition-all active:scale-95"
                >
                  <span>Go to Library</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Phone Camera Connect Assistant Modal */}
      {isMobileCamModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
          <div className="w-full max-w-3xl bg-brand-surface rounded-3xl border border-brand-border shadow-2xl p-6 md:p-8 space-y-6 max-h-[92vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-purple-600/20 border border-purple-500/40 text-purple-400 flex items-center justify-center">
                  <Smartphone className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-brand-text">
                    Use Mobile Phone as Camera (iPhone / Android)
                  </h2>
                  <p className="text-xs text-brand-muted mt-0.5">
                    Connect your phone wirelessly or via USB cable to use its high-resolution 4K/1080p camera as Host or Guest Cam.
                  </p>
                </div>
              </div>

              <button
                onClick={() => setIsMobileCamModalOpen(false)}
                className="p-1.5 rounded-lg text-brand-muted hover:text-brand-text cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Currently Detected Phone Cameras Section */}
            <div className="p-4 rounded-2xl bg-brand-elevated border border-brand-border/60 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-brand-text flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Detected Cameras on this PC ({cameraDevices.length})</span>
                </span>
                <button
                  onClick={refreshDevices}
                  disabled={isRefreshingDevices}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-brand-surface hover:bg-brand-surface/80 border border-brand-border text-xs text-brand-text font-semibold transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRefreshingDevices ? 'animate-spin text-brand-accent' : ''}`} />
                  <span>Scan & Refresh</span>
                </button>
              </div>

              <div className="space-y-2">
                {cameraDevices.filter(c => isPhoneCam(c.label)).length > 0 ? (
                  cameraDevices.filter(c => isPhoneCam(c.label)).map(c => (
                    <div
                      key={c.deviceId}
                      className="flex items-center justify-between p-3 rounded-xl bg-purple-950/20 border border-purple-500/40 text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 text-purple-400" />
                        <span className="font-bold text-white">{c.label}</span>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-mono text-[10px] font-semibold">
                          Active & Ready
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedHostCameraId(c.deviceId);
                            setIsMobileCamModalOpen(false);
                          }}
                          className="px-2.5 py-1 rounded-lg bg-brand-accent hover:bg-brand-accent-hover text-white text-[11px] font-bold transition-all"
                        >
                          Use as Host Cam
                        </button>
                        <button
                          onClick={() => {
                            setSelectedGuestCameraId(c.deviceId);
                            if (formatMode !== 'vodcast-duo') setFormatMode('vodcast-duo');
                            setIsMobileCamModalOpen(false);
                          }}
                          className="px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold transition-all"
                        >
                          Use as Guest Cam
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-3 rounded-xl bg-brand-surface border border-brand-border/40 text-xs text-brand-muted flex items-center justify-between">
                    <span>No active phone camera driver detected yet. Follow one of the 2 methods below to connect your phone:</span>
                  </div>
                )}
              </div>
            </div>

            {/* 2 Easy Connection Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* METHOD 1: Windows 11 Native (Link to Windows / Connected Camera) */}
              <div className="p-5 rounded-2xl bg-brand-surface border border-brand-border/80 flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-brand-accent font-bold text-xs uppercase tracking-wider">
                    <Wifi className="w-4 h-4" />
                    <span>Method 1: Windows 11 Native (Wireless)</span>
                  </div>
                  <h3 className="text-sm font-bold text-brand-text">
                    Link to Windows / Connected Camera
                  </h3>
                  <p className="text-xs text-brand-muted leading-relaxed">
                    Agar aapke paas Windows 11 hai, toh aap apne <strong>Samsung Galaxy, Google Pixel, ya Android phone</strong> ko bina kisi extra app ke wirelessly webcam bana sakte hain.
                  </p>
                  <ol className="text-xs text-brand-muted space-y-1.5 list-decimal list-inside pt-1">
                    <li>Neeche diye button se <strong>Windows Settings</strong> open karein.</li>
                    <li><strong>"Mobile devices"</strong> me apna phone link karein.</li>
                    <li><strong>"Use as a connected camera"</strong> switch turn on karein.</li>
                    <li>Wapas aakar <strong>"Scan & Refresh"</strong> dabayein!</li>
                  </ol>
                </div>

                <button
                  onClick={() => handleOpenExternal('ms-settings:mobile-devices')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-semibold shadow-md transition-all cursor-pointer"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  <span>Open Windows Mobile Devices Settings</span>
                </button>
              </div>

              {/* METHOD 2: Free 4K Apps (Iriun / DroidCam - Android & iPhone) */}
              <div className="p-5 rounded-2xl bg-brand-surface border border-brand-border/80 flex flex-col justify-between space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs uppercase tracking-wider">
                    <Usb className="w-4 h-4" />
                    <span>Method 2: USB Cable / Wi-Fi (Android & iPhone)</span>
                  </div>
                  <h3 className="text-sm font-bold text-brand-text">
                    Iriun 4K Webcam ya DroidCam (Recommended)
                  </h3>
                  <p className="text-xs text-brand-muted leading-relaxed">
                    Zero-latency aur crystal-clear 4K / 1080p video ke liye podcasters <strong>Iriun Webcam</strong> ya <strong>DroidCam</strong> use karte hain (USB cable ya same Wi-Fi pe).
                  </p>
                  <div className="p-2.5 rounded-xl bg-purple-950/40 border border-purple-500/30 text-[11px] text-purple-200 flex items-start gap-2">
                    <Mic className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                    <span>
                      <strong>Phone Mic Feature:</strong> DroidCam aur Iriun aapke phone ke crystal-clear microphone ko bhi PC par transfer karte hain. Aap studio me phone ke mic ko Host ya Guest mic ke taur pe select kar sakte hain!
                    </span>
                  </div>
                  <ol className="text-xs text-brand-muted space-y-1.5 list-decimal list-inside pt-1">
                    <li>Phone me Play Store / App Store se <strong>"Iriun 4K Webcam"</strong> install karein.</li>
                    <li>PC par bhi free Iriun client download karein (1-click link neeche).</li>
                    <li>Phone ko USB cable se connect karein ya same Wi-Fi par rakhein.</li>
                    <li>MeetSplit AI phone ko automatically detect kar lega!</li>
                  </ol>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleOpenExternal('https://iriun.com')}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>Download Iriun 4K</span>
                  </button>
                  <button
                    onClick={() => handleOpenExternal('https://www.dev47apps.com/')}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-brand-elevated hover:bg-brand-elevated/80 border border-brand-border text-brand-text text-xs font-semibold transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span>Download DroidCam</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Bottom Actions */}
            <div className="pt-2 border-t border-brand-border/60 flex items-center justify-between">
              <span className="text-[11px] text-brand-muted">
                Phone connect hote hi Camera 1 ya Camera 2 dropdown me select kar sakte hain.
              </span>
              <button
                onClick={() => setIsMobileCamModalOpen(false)}
                className="px-5 py-2 rounded-xl bg-brand-accent hover:bg-brand-accent-hover text-white text-xs font-bold transition-all"
              >
                Close & Return to Studio
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
